import { CheckCircle2, Lightbulb } from 'lucide-react';
import type { ReactNode } from 'react';

const OPTION_BASE = 'rounded-2xl border px-4 py-2.5 text-left text-sm font-extrabold';
const OPTION_ON = 'border-ink bg-ink text-white';
const OPTION_OFF = 'border-hairline bg-surface text-ink hover:border-ink/40';

export function OptionButton({
  pressed,
  onClick,
  children,
  disabled = false,
}: {
  pressed: boolean;
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      className={`${OPTION_BASE} ${pressed ? OPTION_ON : OPTION_OFF} disabled:cursor-not-allowed disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

export interface CaseListItem {
  id: string;
  label: string;
  detail?: string;
}

export function CaseList({
  heading,
  label,
  items,
  selectedId,
  onSelect,
}: {
  heading: string;
  label: string;
  items: CaseListItem[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <aside className="min-w-0 rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft">
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">{heading}</p>
      <div className="mt-3 grid gap-2" role="group" aria-label={label}>
        {items.map((item) => (
          <OptionButton
            key={item.id}
            pressed={selectedId === item.id}
            onClick={() => onSelect(item.id)}
          >
            <span className="block">{item.label}</span>
            {item.detail ? (
              <span
                className={`mt-0.5 block text-xs font-bold ${selectedId === item.id ? 'text-white/80' : 'text-slate2'}`}
              >
                {item.detail}
              </span>
            ) : null}
          </OptionButton>
        ))}
      </div>
    </aside>
  );
}

export function FeedbackPanel({
  good,
  title,
  label,
  children,
}: {
  good: boolean;
  title: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <div
      role="status"
      aria-label={label}
      className={`rounded-2xl border p-4 ${good ? 'border-brand-mint/40 bg-wash-mint' : 'border-brand-coral/35 bg-wash-coral'}`}
    >
      <p className="flex items-center gap-2 font-extrabold text-ink">
        {good ? (
          <CheckCircle2 className="h-5 w-5 shrink-0 text-brand-mint" aria-hidden="true" />
        ) : (
          <Lightbulb className="h-5 w-5 shrink-0 text-brand-coral" aria-hidden="true" />
        )}
        {title}
      </p>
      <div className="mt-1 text-sm font-medium leading-6 text-slate2">{children}</div>
    </div>
  );
}

/** Horizontal bar driven by a model value. The value is always printed, so colour is not the cue. */
export function ValueBar({
  label,
  value,
  max,
  valueText,
  highlight,
  tone,
}: {
  label: string;
  value: number;
  max: number;
  valueText: string;
  highlight: boolean;
  tone: 'sky' | 'coral' | 'mint';
}) {
  const percent = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  const fill = { sky: 'bg-brand-sky', coral: 'bg-brand-coral', mint: 'bg-brand-mint' }[tone];
  return (
    <div className={`grid grid-cols-[minmax(0,7.5rem)_minmax(0,1fr)] items-center gap-2 sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)] ${highlight ? 'font-extrabold text-ink' : 'font-bold text-slate2'}`}>
      <span className="truncate text-xs">
        {highlight ? '▶ ' : ''}
        {label}
      </span>
      <div className="flex min-w-0 items-center gap-2">
        <div className="h-3 min-w-0 flex-1 overflow-hidden rounded-full bg-surface" aria-hidden="true">
          <div
            className={`h-full rounded-full ${fill} ${highlight ? '' : 'opacity-50'}`}
            style={{ width: `${percent}%` }}
          />
        </div>
        <span className="w-16 shrink-0 text-right text-xs tabular-nums">{valueText}</span>
      </div>
    </div>
  );
}
